/**
 * Θέμα εφαρμογής (ανοιχτό / σκούρο).
 *
 * Η επιλογή είναι ΑΝΑ ΣΥΣΚΕΥΗ, όχι ανά χρήστη: το ίδιο ταμείο το χειρίζονται
 * πολλοί υπάλληλοι με διαφορετικά PIN μέσα στη βάρδια, και το θέμα εξαρτάται
 * από την οθόνη και τον φωτισμό του καταστήματος — όχι από το ποιος στέκεται
 * μπροστά της. Γι' αυτό localStorage και όχι ρύθμιση λογαριασμού.
 *
 * Προεπιλογή: 'dark'. Η εφαρμογή είναι σήμερα 100% σκούρα, οπότε ό,τι δεν έχει
 * ακόμα μεταφερθεί στα tokens παραμένει σωστό. Όταν ολοκληρωθεί η μετάβαση,
 * η προεπιλογή μπορεί να γίνει 'system'.
 *
 * Εμφάνιση (12/10/2026, απόφαση ιδιοκτήτη): η «Νέα εμφάνιση» είναι πλέον Η εμφάνιση της
 * εφαρμογής — παντού, χωρίς διακόπτη. Το `look` μένει στο context ('v2' πάντα μέσα στον
 * provider) μόνο για να μη χρειαστεί να ξαναγραφτούν αμέσως τα ~40 σημεία που διακλαδώνουν
 * σε «κλασική/νέα»· ο κλασικός κώδικας είναι νεκρός και φεύγει σε ξεχωριστό καθάρισμα.
 * Ό,τι άλλαζε ο παλιός διακόπτης στον ΚΑΤΑΛΟΓΟ ζει πλέον ως ρύθμιση συσκευής του ταμείου
 * («Κατάλογος: μία οθόνη / σε βήματα», utils/catalogPrefsV2).
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { DEFAULT_APPEARANCE, getStoreDefaultAppearance, subscribeStoreTheme } from '../utils/storeTheme';

export type Theme = 'light' | 'dark' | 'system';
export type Look = 'classic' | 'v2';

const STORAGE_KEY = 'xentrix_theme';

interface ThemeContextValue {
  /** Τι έχει επιλέξει ο χρήστης (μπορεί να είναι 'system') */
  theme: Theme;
  /** Τι ισχύει πραγματικά τώρα — ποτέ 'system' */
  resolved: 'light' | 'dark';
  setTheme: (t: Theme) => void;
  toggle: () => void;
  /** Πάντα 'v2' μέσα στον provider. Εκτός provider (μόνο tests παλιών ροών) 'classic'. */
  look: Look;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const systemPrefersDark = (): boolean => {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
  } catch {
    return true;
  }
};

/** Η επιλογή ΤΗΣ ΣΥΣΚΕΥΗΣ, ή null αν δεν έχει διαλέξει ποτέ (τότε μετρά το κατάστημα). */
const readDeviceChoice = (): Theme | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / αποκλεισμένο storage — πέφτουμε στην προεπιλογή */
  }
  return null;
};

// Χωρίς επιλογή συσκευής: η προεπιλογή του καταστήματος (θέμα οργανισμού/καταστήματος, 12/10/2026:
// utils/storeTheme — διαβάζεται και από το cache της τελευταίας σύνδεσης), αλλιώς η προεπιλογή εφαρμογής
// (ανοιχτό — απόφαση ιδιοκτήτη 15/10/2026· ως τότε σκούρο).
const readStored = (): Theme => readDeviceChoice() ?? getStoreDefaultAppearance() ?? DEFAULT_APPEARANCE;

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() =>
    readStored() === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : (readStored() as 'light' | 'dark')
  );

  // Εφαρμογή στο <html>: η κλάση .dark είναι αυτό που διαβάζει το Tailwind
  // (darkMode: ["class"]) και τα CSS variables του index.css.
  useEffect(() => {
    const next = theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme;
    setResolved(next);
    const root = document.documentElement;
    root.classList.toggle('dark', next === 'dark');
    root.style.colorScheme = next;
  }, [theme]);

  // Όσο η επιλογή είναι 'system', ακολουθούμε ζωντανά τις αλλαγές του λειτουργικού.
  useEffect(() => {
    if (theme !== 'system') return;
    let mq: MediaQueryList;
    try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { return; }
    const onChange = () => {
      const next = mq.matches ? 'dark' : 'light';
      setResolved(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.style.colorScheme = next;
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);

  // Όσο η συσκευή δεν έχει δική της επιλογή, ακολουθεί το θέμα του καταστήματος (φόρτωση μετά το
  // /me, αλλαγή από τις ρυθμίσεις, άλλο κατάστημα στη σύνδεση).
  useEffect(() => subscribeStoreTheme(() => {
    if (readDeviceChoice() === null) setThemeState(getStoreDefaultAppearance() ?? DEFAULT_APPEARANCE);
  }), []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* δεν είναι κρίσιμο */ }
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  // Η κλάση .look-v2 στο <html> αλλάζει τα tokens (index.css)· την βάζει και το pre-paint script
  // του public/index.html, ώστε να μην αλλάζουν τα γκρι μετά το φόρτωμα.
  useEffect(() => {
    document.documentElement.classList.add('look-v2');
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle, look: 'v2' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Αντί για throw: ένα component εκτός provider δεν πρέπει να ρίχνει το ταμείο.
    return { theme: 'dark', resolved: 'dark', setTheme: () => {}, toggle: () => {}, look: 'classic' };
  }
  return ctx;
};

export default ThemeContext;
